import type { NavMeConfig } from '../types';
import { edgeFunctionUrl } from './supabaseClient';

/**
 * Plain REST client for MultiSet VPS — deliberately independent of the
 * @multisetai/vps WebXR SDK and of navigator.xr entirely. This is what lets
 * cameraArSession.ts localize on iOS Safari, which doesn't implement the
 * WebXR Device API (see README "iOS support" section).
 *
 * Endpoints/shapes confirmed against docs.multiset.ai/basics/rest-api-docs
 * (Authentication + Map Query pages) on 2026-07-09 — nothing invented.
 * Same CORS domain whitelist as the WebXR SDK applies here (MultiSet
 * dashboard → Credentials → Settings → Domains).
 */

const API_BASE = 'https://api.multiset.ai/v1';

export interface CameraIntrinsics {
  fx: number;
  fy: number;
  px: number;
  py: number;
}

export interface MapQueryResult {
  poseFound: boolean;
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number; w: number };
  confidence?: number;
  mapIds?: string[];
  mapCodes?: string[];
  responseTime?: number;
}

interface TokenResponse {
  token: string;
  expiresOn: string;
  error?: string;
}

export interface MapDetails {
  _id: string;
  accountId: string;
  mapName: string;
  mapCode: string;
  status: 'uploading' | 'pending' | 'active' | 'failed';
  /** GeoJSON Point, coordinates = [longitude, latitude, altitude]. */
  location?: { type: string; coordinates: number[] } | null;
}

export class MultisetRestClient {
  private token: string | null = null;
  private expiresOn = 0; // epoch ms

  constructor(private config: NavMeConfig) {}

  /** Fetches (or reuses) a bearer token. Tokens last 30 min per MultiSet docs. */
  async authorize(): Promise<void> {
    if (this.token && Date.now() < this.expiresOn - 30_000) return; // 30s safety margin

    // Brokered mode: exchange the map code for a short-lived token via the
    // multiset-token Edge Function (credentials stay server-side — customer
    // BYO account or the platform account, the broker decides). Direct mode
    // remains for consoles validating freshly typed credentials.
    if (this.config.brokered) {
      const res = await fetch(edgeFunctionUrl('multiset-token'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapCode: this.config.code }),
      });
      if (!res.ok) throw new Error(await restErrorMessage(res));
      const data = (await res.json()) as TokenResponse;
      if (data.error) throw new Error(data.error);
      this.token = data.token;
      this.expiresOn = Date.parse(data.expiresOn);
      return;
    }

    const authorization = 'Basic ' + btoa(`${this.config.clientId}:${this.config.clientSecret}`);
    const res = await fetch(`${API_BASE}/m2m/token`, {
      method: 'POST',
      headers: {
        Authorization: authorization,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        clientId: this.config.clientId,
        clientSecret: this.config.clientSecret,
      }),
    });

    if (!res.ok) {
      throw new Error(await restErrorMessage(res));
    }
    const data = (await res.json()) as TokenResponse;
    if (data.error) throw new Error(data.error);

    this.token = data.token;
    this.expiresOn = Date.parse(data.expiresOn);
  }

  /**
   * Single-image VPS localization. `queryImage` is a base64 data URL
   * (e.g. "data:image/jpeg;base64,...") as required by the API.
   */
  async queryMap(
    queryImage: string,
    intrinsics: CameraIntrinsics,
    resolution: { width: number; height: number },
    opts: { isRightHanded?: boolean } = {},
  ): Promise<MapQueryResult> {
    await this.authorize();

    const body: Record<string, unknown> = {
      cameraIntrinsics: intrinsics,
      resolution,
      queryImage,
      isRightHanded: opts.isRightHanded ?? true,
    };
    if (this.config.mapType === 'map-set') {
      body.mapSetCode = this.config.code;
    } else {
      body.mapCode = this.config.code;
    }

    const res = await fetch(`${API_BASE}/vps/map/query`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(await restErrorMessage(res));
    }
    return (await res.json()) as MapQueryResult;
  }

  /**
   * GET /vps/map — paginated map list, documented response is
   * {data: [...], pagination: {totalPages, ...}} (docs page "Map details" →
   * "Get all VPS maps", re-verified 2026-07-11). NOTE: the documented row
   * example carries only id/name/status — no mapCode — so extractCode()
   * additionally scans every string field for a MAP_* value. Rows without a
   * recoverable code are still returned (mapCode: '') so the UI can show
   * them and explain, rather than silently dropping the account's maps.
   */
  async listMaps(): Promise<Array<{ mapName: string; mapCode: string; status: string }>> {
    const rows = await this.fetchAllPages('/vps/map');
    return rows.map((r) => ({
      mapName: String(r.mapName ?? r.name ?? r.map_name ?? 'Map'),
      mapCode: extractCode(r, 'MAP_'),
      status: String(r.status ?? 'active').toLowerCase(),
    }));
  }

  /** GET /vps/map-set — same paginated {data, pagination} envelope. */
  async listMapSets(): Promise<Array<{ name: string; code: string }>> {
    const rows = await this.fetchAllPages('/vps/map-set');
    return rows
      .map((r) => ({
        name: String(r.mapSetName ?? r.name ?? r.mapsetName ?? 'MapSet'),
        code: extractCode(r, 'MSET_'),
      }))
      .filter((r) => r.code);
  }

  /**
   * Drains a paginated list endpoint (?limit=100&page=N) following the
   * documented pagination.totalPages; hard-capped at 10 pages (1000 rows).
   * Logs a sample row per endpoint so debug output shows the real shape.
   */
  private async fetchAllPages(path: string): Promise<Array<Record<string, unknown>>> {
    await this.authorize();
    const out: Array<Record<string, unknown>> = [];
    for (let page = 1; page <= 10; page++) {
      const res = await fetch(`${API_BASE}${path}?limit=100&page=${page}`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) throw new Error(await restErrorMessage(res));
      const body = (await res.json()) as {
        pagination?: { totalPages?: number };
      };
      const rows = unwrapList(body);
      if (page === 1 && rows.length > 0) {
        console.info(`MultiSet ${path} sample row:`, JSON.stringify(rows[0]));
      }
      out.push(...rows);
      if (rows.length === 0 || page >= Number(body?.pagination?.totalPages ?? 1)) break;
    }
    return out;
  }

  /**
   * POST /v2/vps/map — creates a map entry and returns presigned S3
   * multipart URLs (documented REST API Docs → Map Upload, verified
   * 2026-07-11). Accepted providers: unity|matterport|leica|navvis|xgrid|
   * faro; fileType zip|e57; files up to 25 GB.
   */
  async createMapUpload(meta: {
    mapName: string;
    fileSize: number;
    coordinates: { latitude: number; longitude: number; altitude: number };
    heading?: number;
    source: { provider: string; fileType: string; coordinateSystem: 'RHS' | 'LHS' };
  }): Promise<MapUploadTicket> {
    await this.authorize();
    const res = await fetch('https://api.multiset.ai/v2/vps/map', {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    });
    if (!res.ok) throw new Error(await restErrorMessage(res));
    return (await res.json()) as MapUploadTicket;
  }

  /**
   * POST /v1/vps/map with fileType "matterpak" — cloud-fetch: MultiSet pulls
   * the MatterPak from the Matterport account (spaceId + OAuth token OR
   * client id/secret); no file upload. Documented: REST API Docs → Map
   * Upload → MatterPak Upload (verified 2026-07-11).
   */
  async createMatterPakMap(meta: {
    mapName: string;
    coordinates: { latitude: number; longitude: number; altitude: number };
    heading?: number;
    metadata: { spaceId: string; oauthToken?: string; clientId?: string; clientSecret?: string };
  }): Promise<{ mapId: string; status: string; mapName: string }> {
    await this.authorize();
    const res = await fetch(`${API_BASE}/vps/map`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mapName: meta.mapName,
        coordinates: meta.coordinates,
        ...(meta.heading !== undefined ? { heading: meta.heading } : {}),
        source: {
          provider: 'matterport',
          fileType: 'matterpak',
          coordinateSystem: 'RHS',
          processType: 'cloud',
          metadata: meta.metadata,
        },
      }),
    });
    if (!res.ok) throw new Error(await restErrorMessage(res));
    return (await res.json()) as { mapId: string; status: string; mapName: string };
  }

  /** POST /v2/vps/map/complete-upload/{mapId} — finalizes the multipart upload. */
  async completeMapUpload(
    mapId: string,
    body: { uploadId: string; key: string; parts: Array<{ ETag: string; PartNumber: number }> },
  ): Promise<void> {
    await this.authorize();
    const res = await fetch(
      `https://api.multiset.ai/v2/vps/map/complete-upload/${encodeURIComponent(mapId)}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) throw new Error(await restErrorMessage(res));
  }

  /**
   * GET /vps/map/{mapCode} — confirmed 2026-07-09 against
   * docs.multiset.ai/basics/rest-api-docs/map-operations. Only valid for
   * single-map mode; MapSet map-detail lookup isn't wired up here.
   */
  async getMapDetails(mapCode: string): Promise<MapDetails> {
    await this.authorize();
    const res = await fetch(`${API_BASE}/vps/map/${encodeURIComponent(mapCode)}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!res.ok) throw new Error(await restErrorMessage(res));
    return (await res.json()) as MapDetails;
  }

  /**
   * GET /file?key=... — returns a pre-signed, time-limited S3 URL for
   * downloading a stored file (e.g. a map's textured mesh). Confirmed
   * 2026-07-09 against the same docs page.
   */
  async getFileDownloadUrl(key: string): Promise<string> {
    await this.authorize();
    const res = await fetch(`${API_BASE}/file?key=${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    if (!res.ok) throw new Error(await restErrorMessage(res));
    const data = (await res.json()) as { url: string };
    return data.url;
  }

  /**
   * Resolves this map's auto-generated mesh (used by AdminScreen's 3D
   * preview). Two filenames are confirmed in MultiSet's own docs for the
   * same `<accountId>/<mapId>/Mesh/` folder: `TexturedMesh.glb` (REST API
   * docs, map-operations.md — `model/gltf-binary`) and `TexturedMesh.obj`
   * (Quest SDK's Map Mesh Downloader docs, which describes the download
   * hierarchy as `MapSpace/MAP_CODE/TexturedMesh.obj`). Which one actually
   * exists for a given map may depend on the map's processing pipeline/age,
   * so both are tried before giving up — this is trying two *documented*
   * filenames, not inventing one.
   *
   * IMPORTANT: this only swallows a genuine "file not found" for each
   * candidate — a failure at the map-details lookup step (bad map code,
   * auth error, network error) propagates as a real thrown error, so
   * callers can surface the actual cause instead of a generic "no mesh"
   * message.
   */
  async getMapMeshUrl(): Promise<{ url: string; format: 'glb' | 'obj' } | null> {
    if (this.config.mapType !== 'map') return null; // TODO(production): MapSet mesh lookup

    const details = await this.getMapDetails(this.config.code); // let real errors propagate
    const candidates: Array<{ filename: string; format: 'glb' | 'obj' }> = [
      { filename: 'TexturedMesh.glb', format: 'glb' },
      { filename: 'TexturedMesh.obj', format: 'obj' },
    ];
    for (const { filename, format } of candidates) {
      const key = `${details.accountId}/${details._id}/Mesh/${filename}`;
      try {
        const url = await this.getFileDownloadUrl(key);
        return { url, format };
      } catch (err) {
        // Logged (not swallowed silently) so a real device's console shows
        // exactly which key/format was tried and why it failed.
        console.warn(`getMapMeshUrl: no mesh at key "${key}" —`, err);
        continue; // try the next known filename
      }
    }
    return null; // neither known filename exists for this map
  }
}

/**
 * Finds the row's public code. Tries the known field names first, then scans
 * every string value for the code prefix (MAP_/MSET_) — needed because the
 * documented list-row example exposes only id/name/status, while the
 * developer portal shows codes, so the field name is uncertain.
 */
function extractCode(row: Record<string, unknown>, prefix: 'MAP_' | 'MSET_'): string {
  for (const key of ['mapCode', 'mapSetCode', 'code', 'map_code', 'mapsetCode', 'vpsMapCode']) {
    const v = row[key];
    if (typeof v === 'string' && v.toUpperCase().startsWith(prefix)) return v;
  }
  for (const v of Object.values(row)) {
    if (typeof v === 'string' && v.toUpperCase().startsWith(prefix)) return v;
  }
  return '';
}

/**
 * Accepts a bare array or the common list envelopes ({maps|mapSets|data|
 * results|items: [...]}) and returns the row array. Logs the shape it saw so
 * a debug report shows exactly what the API returned when the list is empty.
 */
function unwrapList(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    for (const key of ['maps', 'mapSets', 'mapsets', 'data', 'results', 'items', 'list']) {
      if (Array.isArray(obj[key])) return obj[key] as Array<Record<string, unknown>>;
    }
    console.warn('MultiSet list: unrecognized envelope, keys =', Object.keys(obj));
  }
  return [];
}

export interface MapUploadTicket {
  mapCode: string;
  mapId: string;
  key: string;
  uploadUrls: { uploadId: string; signedUrls: Array<{ partNumber: number; signedUrl: string }> };
}

async function restErrorMessage(res: Response): Promise<string> {
  // Keep the WHOLE response body (truncated) — MultiSet's error/message
  // fields are often generic while the real reason sits elsewhere in the
  // payload (upload-failure diagnosis was impossible with just "HTTP 400").
  try {
    const text = await res.text();
    try {
      const data = JSON.parse(text) as { error?: string; message?: string };
      const lead = data.error || data.message;
      const body = text.length > 300 ? `${text.slice(0, 300)}…` : text;
      return lead ? `${lead} (HTTP ${res.status}) — ${body}` : `HTTP ${res.status} — ${body}`;
    } catch {
      return `HTTP ${res.status}${text ? ` — ${text.slice(0, 300)}` : ''}`;
    }
  } catch {
    return `HTTP ${res.status}`;
  }
}
